import React, { lazy, Suspense, useLayoutEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useReducedMotion } from "framer-motion";
import { useTheatre } from "./hooks/useTheatre";
import Navigation from "./components/common/Navigation";
import Footer from "./components/common/Footer";
import ScrollToTop from "./components/common/ScrollToTop";
import LanguageWrapper from "./components/common/LanguageWrapper";
import Home from "./pages/Home";
import "./App.css";
import "./styles/globals.css";
import "./styles/studio-public.css";
import { loadNamespaces } from "./i18n";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

/* Every route but the homepage is its own chunk. Before this, the first
   visit to any page downloaded and parsed every page: all blog post
   bodies (~300 KB), the apps copy, `marked`, the admin editor and the
   icons each of them uses — a 1 MB bundle whose execution was the
   single largest cost of mobile LCP. `/` stays eager because it is
   where most visits land.

   A route chunk and the translation namespaces it reads arrive together,
   so a cold landing never paints raw keys. The shell's namespaces
   (common, studio, projects) are already loaded; list only the rest. */
const page = (load, namespaces = []) =>
  lazy(() =>
    Promise.all([load(), loadNamespaces(namespaces)]).then(([module]) => module),
  );

const Services = page(() => import("./pages/Services"));
const SEO = page(() => import("./pages/services/SEO"), ["seoPage"]);
const GEO = page(() => import("./pages/services/GEO"), ["geoPage"]);
const CRO = page(() => import("./pages/services/CRO"), ["croPage"]);
const Shopify = page(() => import("./pages/services/Shopify"), ["shopifyPage"]);
const ShopifyPortfolio = page(() => import("./pages/ShopifyPortfolio"), ["shopifyPortfolio"]);
const SaaS = page(() => import("./pages/services/SaaS"), ["saasPage"]);
const CreativeEngine = page(() => import("./pages/services/CreativeEngine"), ["creativeEnginePage"]);
const AIImplementation = page(() => import("./pages/services/AIImplementation"), ["aiImplementationPage"]);
const AIAgents = page(() => import("./pages/services/AIAgents"), ["aiAgentsPage"]);
const PhpSecurity = page(() => import("./pages/PhpSecurity"), ["phpSecurityPage"]);
const About = page(() => import("./pages/About"), ["about"]);
const Clients = page(() => import("./pages/Clients"));
const RequestQuote = page(() => import("./pages/RequestQuote"), ["quote"]);
const Blog = page(() => import("./pages/Blog"), ["blog"]);
const BlogPost = page(() => import("./pages/BlogPost"), ["blog"]);
const Projects = page(() => import("./pages/Projects"));
const ProjectDetail = page(() => import("./pages/ProjectDetail"));
const NotFound = page(() => import("./pages/NotFound"), ["notFound"]);
const Apps = page(() => import("./pages/apps/Apps"));
const RuleForge = page(() => import("./pages/apps/RuleForge"));
const RuleForgeDocument = page(() => import("./pages/apps/RuleForgeDocument"));
const Login = page(() => import("./pages/admin/Login"));
const BlogManager = page(() => import("./pages/admin/BlogManager"));
const BlogEditor = page(() => import("./pages/admin/BlogEditor"));

/* Holds the viewport while a route chunk arrives, so the footer does not
   jump up under the header and back down (layout shift). React Router
   wraps navigations in a transition, so in-app links keep the current
   page on screen instead — this only shows on a cold landing. */
const routeFallback = <div style={{ minHeight: "100vh" }} aria-hidden="true" />;

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0a0a0a",
          color: "#888",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

// Layout wrapper that conditionally shows Nav/Footer
function AppLayout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const mainRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useLayoutEffect(() => {
    // The homepage already owns two canvas films and a WebGL scene. Avoid an
    // additional animated opacity layer around its sticky canvas surfaces.
    const isHome = location.pathname === '/' || /^\/es\/?$/.test(location.pathname);
    if (isAdminRoute || isHome || reduceMotion || document.hidden) return;
    // An opacity-only arrival preserves sticky positioning and keeps content
    // readable from the first paint. Cancel before the next route takes over.
    const animation = mainRef.current?.animate?.(
      [{ opacity: 0.72 }, { opacity: 1 }],
      { duration: 240, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    );
    return () => animation?.cancel();
  }, [location.pathname, isAdminRoute, reduceMotion]);

  // Initialize Theatre.js (loads Studio in development mode)
  useTheatre();

  if (isAdminRoute) {
    return <>{children}</>;
  }

  return (
    <div className="app studio-public">
      <a href="#main-content" className="sr-only sr-only-focusable">
        Skip to main content
      </a>
      <Navigation />
      <main id="main-content" ref={mainRef}>
        {children}
      </main>
      <Footer />
    </div>
  );
}

/* The route table below is written out twice — once per locale — rather
   than shared through a helper. A `<PublicRoutes />` component used to
   sit here claiming to "avoid repetition"; nothing ever mounted it, and
   by the time it was deleted it had silently fallen four routes behind
   the live tables. React Router only accepts `<Route>` as a direct child
   of `<Routes>`, so a component wrapper could never have worked. Two
   explicit lists that the compiler checks beat one abstraction that
   drifts. */

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <LanguageWrapper>
            <ScrollToTop />
            <AppLayout>
              <Suspense fallback={routeFallback}>
                <Routes>
                  {/* English routes (default) */}
                  <Route path="/" element={<Home />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/seo" element={<SEO />} />
                  <Route path="/services/geo" element={<GEO />} />
                  <Route path="/services/cro" element={<CRO />} />
                  <Route path="/services/shopify" element={<Shopify />} />
                  <Route
                    path="/shopify-portfolio"
                    element={<ShopifyPortfolio />}
                  />
                  <Route path="/services/saas" element={<SaaS />} />
                  <Route
                    path="/services/creative-engine"
                    element={<CreativeEngine />}
                  />
                  <Route
                    path="/services/ai-implementation"
                    element={<AIImplementation />}
                  />
                  <Route path="/services/ai-agents" element={<AIAgents />} />
                  <Route path="/php-security" element={<PhpSecurity />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/quote" element={<RequestQuote />} />
                  <Route
                    path="/contact"
                    element={<Navigate to="/quote" replace />}
                  />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/:slug" element={<ProjectDetail />} />
                  <Route path="/apps" element={<Apps />} />
                  <Route path="/apps/ruleforge" element={<RuleForge />} />
                  <Route
                    path="/apps/ruleforge/support"
                    element={<RuleForgeDocument type="support" />}
                  />
                  <Route
                    path="/apps/ruleforge/privacy"
                    element={<RuleForgeDocument type="privacy" />}
                  />
                  <Route
                    path="/apps/ruleforge/terms"
                    element={<RuleForgeDocument type="terms" />}
                  />

                  {/* Spanish routes (/es prefix) */}
                  <Route path="/es" element={<Home />} />
                  <Route path="/es/services" element={<Services />} />
                  <Route path="/es/services/seo" element={<SEO />} />
                  <Route path="/es/services/geo" element={<GEO />} />
                  <Route path="/es/services/cro" element={<CRO />} />
                  <Route path="/es/services/shopify" element={<Shopify />} />
                  <Route
                    path="/es/shopify-portfolio"
                    element={<ShopifyPortfolio />}
                  />
                  <Route path="/es/services/saas" element={<SaaS />} />
                  <Route
                    path="/es/services/creative-engine"
                    element={<CreativeEngine />}
                  />
                  <Route
                    path="/es/services/ai-implementation"
                    element={<AIImplementation />}
                  />
                  <Route path="/es/services/ai-agents" element={<AIAgents />} />
                  <Route path="/es/php-security" element={<PhpSecurity />} />
                  <Route path="/es/about" element={<About />} />
                  <Route path="/es/clients" element={<Clients />} />
                  <Route path="/es/quote" element={<RequestQuote />} />
                  <Route
                    path="/es/contact"
                    element={<Navigate to="/es/quote" replace />}
                  />
                  <Route path="/es/blog" element={<Blog />} />
                  <Route path="/es/blog/:slug" element={<BlogPost />} />
                  <Route path="/es/projects" element={<Projects />} />
                  <Route path="/es/projects/:slug" element={<ProjectDetail />} />
                  <Route path="/es/apps" element={<Apps />} />
                  <Route path="/es/apps/ruleforge" element={<RuleForge />} />
                  <Route
                    path="/es/apps/ruleforge/support"
                    element={<RuleForgeDocument type="support" />}
                  />
                  <Route
                    path="/es/apps/ruleforge/privacy"
                    element={<RuleForgeDocument type="privacy" />}
                  />
                  <Route
                    path="/es/apps/ruleforge/terms"
                    element={<RuleForgeDocument type="terms" />}
                  />

                  {/* Admin routes (English only) */}
                  <Route path="/admin/login" element={<Login />} />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <BlogManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/posts/new"
                    element={
                      <ProtectedRoute>
                        <BlogEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/posts/:slug/edit"
                    element={
                      <ProtectedRoute>
                        <BlogEditor />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AppLayout>
          </LanguageWrapper>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}
